import { Heart, MessageCircle, User, Trash2, Loader2 } from '@/ui/icons'
import { Photo } from '@/shared/api/photos'
import { cn } from '@/shared/utils'
import { Link } from 'react-router-dom'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import { Badge } from '@/ui/components'
import { useAuth } from '@/modules/auth'
import { useState } from 'react'

/**
 * Get optimized thumbnail URL for grid view
 * Uses .webp versions created by Lambda optimization when available
 * Only uses .webp when optimization is completed, otherwise uses original
 */
function getOptimizedThumbnailUrl(url: string, optimizationStatus?: string): string {
  if (!url || !url.includes('supabase.co')) {
    return url
  }
  
  // Only use .webp if optimization is completed
  // Otherwise, use original URL so user sees image immediately
  if (optimizationStatus !== 'completed') {
    return url
  }
  
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    
    // Replace image extensions with .webp (handles jpg, jpeg, png, heic, heif)
    // This uses the optimized .webp files created by AWS Lambda
    const webpPathname = pathname.replace(/\.(jpg|jpeg|png|heic|heif)$/i, '.webp')
    
    // Only use .webp if the extension was actually replaced (i.e., it was an image)
    if (webpPathname !== pathname) {
      urlObj.pathname = webpPathname
    }
    
    // Remove any query params (Supabase Storage doesn't support on-the-fly transformations)
    urlObj.search = ''
    
    return urlObj.toString()
  } catch {
    return url
  }
}

interface PhotoCardProps {
  photo: Photo
  onClick: (photo: Photo) => void
  onDeletePhoto?: (photoId: string) => Promise<void>
  className?: string
  coverPhotoId?: string | null
  isDeleting?: boolean
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
  if (email) {
    return email.substring(0, 2).toUpperCase()
  }
  return 'U'
}

function getAvatarColor(userId: string): string {
  const colors = [
    'bg-adventure-100 text-adventure-700',
    'bg-earth-100 text-earth-700',
    'bg-gray-100 text-gray-700',
    'bg-adventure-200 text-adventure-800',
    'bg-earth-200 text-earth-800',
    'bg-gray-200 text-gray-800'
  ]
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

function formatDate(dateString?: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function PhotoCard({ photo, onClick, onDeletePhoto, className, coverPhotoId, isDeleting }: PhotoCardProps) {
  const initials = getInitials(photo.uploaderName, photo.uploaderEmail)
  const avatarColor = photo.uploadedBy ? getAvatarColor(photo.uploadedBy) : 'bg-gray-100 text-gray-700'
  const displayName = photo.uploaderName || photo.uploaderEmail?.split('@')[0] || 'Unknown'
  const timestamp = formatDate(photo.uploadedAt)
  const likeCount = photo.likeCount ?? 0
  const commentCount = photo.commentCount ?? 0
  const isCoverPhoto = coverPhotoId === photo.id
  const { user } = useAuth()
  const [isDeletingLocal, setIsDeletingLocal] = useState(false)
  
  const isOwner = user?.id === photo.uploadedBy
  const showDeleteButton = isOwner && onDeletePhoto
  const showDeleteConfirm = false

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onDeletePhoto) return
    
    if (!confirm('Are you sure you want to delete this photo? This action cannot be undone.')) {
      return
    }
    
    setIsDeletingLocal(true)
    try {
      await onDeletePhoto(photo.id)
    } catch (error) {
    } finally {
      setIsDeletingLocal(false)
    }
  }

  const isDeletingState = isDeleting || isDeletingLocal

  return (
    <div
      className={cn(
        'group cursor-pointer bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow',
        className
      )}
      onClick={() => onClick(photo)}
    >
      {/* Photo */}
      <div className="relative aspect-square bg-gray-100 overflow-hidden">
        {photo.url ? (
          <img
            src={getOptimizedThumbnailUrl(photo.url, photo.optimizationStatus)}
            alt={photo.caption || photo.filename || 'Photo'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              // Fallback to original URL if .webp version doesn't exist
              const target = e.target as HTMLImageElement
              const originalUrl = photo.url
              if (target.src !== originalUrl) {
                target.src = originalUrl
              }
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <span className="text-sm">No image</span>
          </div>
        )}
        
        {/* Cover photo badge */}
        {isCoverPhoto && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="bg-adventure-600 text-white backdrop-blur-sm">
              Cover Photo
            </Badge>
          </div>
        )}
        
        {/* Delete button - only show for photo owner */}
        {showDeleteButton && (
          <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleDelete}
              disabled={isDeletingState}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-white transition-colors',
                isDeletingState 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-red-500 hover:bg-red-600'
              )}
              aria-label="Delete photo"
            >
              {isDeletingState ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200" />
      </div>

      {/* Footer with interaction counts and uploader info */}
      <div className="p-3 space-y-2">
        {/* Interaction counts */}
        {(likeCount > 0 || commentCount > 0) && (
          <div className="flex items-center gap-4 text-sm text-gray-600">
            {likeCount > 0 && (
              <div className="flex items-center gap-1">
                <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                <span>{likeCount}</span>
              </div>
            )}
            {commentCount > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span>{commentCount}</span>
              </div>
            )}
          </div>
        )}

        {/* Uploader info */}
        <div className="flex items-center gap-2">
          {photo.uploadedBy ? (
            <Link to={`/users/${photo.uploadedBy}`} className="flex-shrink-0">
              {photo.uploaderProfileImagePath ? (
                <img
                  src={getUserProfileUrl(photo.uploaderProfileImagePath) || undefined}
                  alt={displayName}
                  className="w-6 h-6 rounded-full object-cover hover:ring-2 hover:ring-adventure-500 transition-all"
                />
              ) : (
                <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold hover:ring-2 hover:ring-adventure-500 transition-all', avatarColor)}>
                  {photo.uploaderName || photo.uploaderEmail ? initials : <User className="w-3 h-3" />}
                </div>
              )}
            </Link>
          ) : (
            <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0', avatarColor)}>
              {photo.uploaderName || photo.uploaderEmail ? initials : <User className="w-3 h-3" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            {photo.uploadedBy ? (
              <Link to={`/users/${photo.uploadedBy}`} className="block">
                <p className="text-sm font-medium text-gray-900 truncate hover:text-adventure-600 transition-colors">{displayName}</p>
              </Link>
            ) : (
              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
            )}
            {timestamp && (
              <p className="text-xs text-gray-500">{timestamp}</p>
            )}
          </div>
        </div>

        {/* Caption preview */}
        {photo.caption && (
          <p className="text-sm text-gray-700 line-clamp-2">{photo.caption}</p>
        )}
      </div>
    </div>
  )
}
